import { useCallback } from "react"
import { UseMutationConfig } from "react-relay"
import { graphql } from "relay-runtime"
import { CustomMutationParams, useMutationWithErrorDialog } from "@/hooks/useMutationWithErrorDialog"
import {
  EditBillingAttributesInput,
  type useEditBillingAttributesMutation as TMutation,
} from "@/modules/patients/detail/conclude/billing-section/services/__generated__/useEditBillingAttributesMutation.graphql"

const editBillingAttributesMutation = graphql`
  mutation useEditBillingAttributesMutation($input: EditBillingAttributesInput!) {
    editBillingAttributes(input: $input) {
      billingAttribute {
        id
        ...BillingAttributeFragment
      }
      errors {
        ... on Error {
          message
        }
      }
    }
  }
`

const useEditBillingAttributesMutation = (params?: CustomMutationParams) => {
  const [commit, isFlight] = useMutationWithErrorDialog<TMutation>(editBillingAttributesMutation, params)

  const commitHandler = useCallback(
    (config: UseMutationConfig<TMutation>) => {
      commit(config)
    },
    [commit],
  )

  return [commitHandler, isFlight] as const
}

const useEditBillingDialogMutation = () => {
  const [editBillingAttributes, isFlight] = useEditBillingAttributesMutation()

  const commit = useCallback(
    (editBillingInput: EditBillingAttributesInput, onCompleted?: () => void) => {
      editBillingAttributes({
        variables: {
          input: editBillingInput,
        },
        updater(store, data) {
          try {
            const billingAttribute = data?.editBillingAttributes.billingAttribute
            const appointmentRecordProxy = store.get(editBillingInput.appointmentId)

            if (billingAttribute?.id) {
              const newRecordProxy = store.get(billingAttribute?.id)
              if (newRecordProxy) {
                appointmentRecordProxy?.setLinkedRecord(newRecordProxy, "billingAttribute")
              }
            }
          } catch (error) {
            console.error("Error while updating Relay store after editBillingAttributes:", error)
          }

          onCompleted?.()
        },
      })
    },
    [editBillingAttributes],
  )

  return [commit, isFlight] as const
}

export { useEditBillingAttributesMutation, useEditBillingDialogMutation }
